(() => {
  'use strict';
  if (window.calvielleSelectReady) return;
  window.calvielleSelectReady = true;
  const instances = new Set(), known = new WeakMap();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  let sequence = 0, active = null, scanFrame;

  class CalvielleSelect {
    constructor(select) {
      this.select = select;
      this.id = `cv-select-${++sequence}`;
      this.oldTabIndex = select.getAttribute('tabindex');
      this.oldAriaHidden = select.getAttribute('aria-hidden');
      this.abort = new AbortController();
      const events = { signal:this.abort.signal };
      this.wrapper = document.createElement('div'); this.wrapper.className = 'cv-select';
      this.button = document.createElement('button'); this.button.type = 'button'; this.button.className = 'cv-select__trigger';
      this.button.setAttribute('role','combobox'); this.button.setAttribute('aria-haspopup','listbox');
      this.button.setAttribute('aria-controls',`${this.id}-list`); this.button.setAttribute('aria-expanded','false');
      this.button.setAttribute('aria-autocomplete','none');
      this.value = document.createElement('span'); this.value.className = 'cv-select__value';
      const arrow = document.createElement('span'); arrow.className = 'cv-select__chevron'; arrow.setAttribute('aria-hidden','true');
      this.button.append(this.value,arrow);
      this.list = document.createElement('div'); this.list.id = `${this.id}-list`; this.list.className = 'cv-select__list';
      this.list.setAttribute('role','listbox'); this.list.hidden = true; this.list.inert = true;
      this.list.setAttribute('aria-hidden','true');
      if (typeof this.list.showPopover === 'function') this.list.setAttribute('popover','manual');
      select.before(this.wrapper); this.wrapper.append(select,this.button,this.list);
      this.sync();
      select.classList.add('cv-select__native'); select.tabIndex = -1; select.setAttribute('aria-hidden','true');
      this.button.addEventListener('click', () => this.opened ? this.close() : this.open(), events);
      this.button.addEventListener('keydown', event => this.key(event), events);
      this.list.addEventListener('pointerdown', event => event.preventDefault(), events);
      this.list.addEventListener('click', event => {
        const option = event.target.closest('[data-option-index]');
        if (option && this.list.contains(option)) this.choose(Number(option.dataset.optionIndex));
      }, events);
      select.addEventListener('change', () => this.sync(), events);
      select.addEventListener('input', () => this.sync(), events);
      select.addEventListener('invalid', event => { event.preventDefault(); this.button.setAttribute('aria-invalid','true'); this.button.focus(); }, events);
      select.form?.addEventListener('reset', () => setTimeout(() => { if(select.isConnected) { this.button.setAttribute('aria-invalid','false'); this.sync(); } }, 0), events);
      this.observer = new MutationObserver(() => { this.sync(); if(this.opened) this.place(); });
      this.observer.observe(select,{attributes:true,childList:true,subtree:true,characterData:true,attributeFilter:['disabled','selected','label','value','hidden','required','aria-label','aria-invalid']});
      known.set(select,this); instances.add(this);
    }

    sync() {
      const select = this.select;
      const label = select.getAttribute('aria-label') || [...select.labels].map(item => item.textContent.trim()).join(' ') || select.name || 'Choose an option';
      this.button.setAttribute('aria-label',label); this.list.setAttribute('aria-label',label);
      this.value.textContent = select.selectedOptions[0]?.label || 'Choose an option';
      const disabled = select.matches(':disabled');
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      this.button.setAttribute('aria-required',String(select.required));
      this.button.setAttribute('aria-invalid',String(select.getAttribute('aria-invalid') === 'true' || (this.button.getAttribute('aria-invalid') === 'true' && !select.validity.valid)));
      this.items = [];
      const fragment = document.createDocumentFragment(); let group = null;
      for (const option of select.options) {
        if (option.hidden || option.parentElement.hidden) continue;
        const nextGroup = option.parentElement.tagName === 'OPTGROUP' ? option.parentElement : null;
        if(nextGroup && nextGroup !== group) { const heading = document.createElement('div'); heading.className = 'cv-select__group'; heading.textContent = nextGroup.label; fragment.append(heading); }
        group = nextGroup;
        const node = document.createElement('div'); node.className = 'cv-select__option'; node.id = `${this.id}-${option.index}`;
        node.dataset.optionIndex = String(option.index); node.setAttribute('role','option'); node.setAttribute('aria-selected',String(option.selected));
        node.setAttribute('aria-disabled',String(option.disabled || !!nextGroup?.disabled)); node.textContent = option.label;
        this.items.push({node,option,disabled:option.disabled || !!nextGroup?.disabled}); fragment.append(node);
      }
      this.list.replaceChildren(fragment);
      const current = this.items.find(item => item.option.index === this.cursor && !item.disabled);
      this.setCursor(current?.option.index ?? select.selectedIndex, false);
      if (disabled && this.opened) this.close(false);
    }

    setCursor(index, scroll = true) {
      const item = this.items.find(item => item.option.index === index && !item.disabled) || this.items.find(item => !item.disabled);
      this.cursor = item?.option.index ?? -1;
      for (const entry of this.items) entry.node.classList.toggle('is-active',entry === item && this.opened);
      if (this.opened && item) this.button.setAttribute('aria-activedescendant',item.node.id);
      else this.button.removeAttribute('aria-activedescendant');
      if (scroll && item && this.opened) {
        const top = item.node.offsetTop, bottom = top + item.node.offsetHeight;
        if (top < this.list.scrollTop) this.list.scrollTop = top;
        else if(bottom > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = bottom - this.list.clientHeight;
      }
    }

    place() {
      const rect = this.button.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0, viewportBottom = viewportTop + (viewport?.height || innerHeight);
      const width = Math.min(Math.max(rect.width,190),document.documentElement.clientWidth - 24);
      this.list.style.width = `${width}px`;
      const below = viewportBottom - rect.bottom - 12, above = rect.top - viewportTop - 12;
      const upward = below < 160 && above > below;
      const height = Math.max(44,Math.min(320,upward ? above : below));
      this.list.style.maxHeight = `${height}px`;
      this.list.style.left = `${Math.max(12,Math.min(rect.left,document.documentElement.clientWidth - width - 12))}px`;
      this.list.style.top = `${upward ? Math.max(viewportTop + 12,rect.top - Math.min(this.list.scrollHeight,height) - 6) : rect.bottom + 6}px`;
    }

    open() {
      if (this.button.disabled) return;
      active?.close(false); clearTimeout(this.closeTimer);
      this.sync(); this.opened = true; active = this;
      this.list.hidden = false; this.list.inert = false; this.list.setAttribute('aria-hidden','false');
      if(this.list.hasAttribute('popover') && !this.list.matches(':popover-open')) this.list.showPopover();
      this.place(); this.button.setAttribute('aria-expanded','true');
      this.setCursor(this.select.selectedIndex);
      this.openFrame = requestAnimationFrame(() => { this.openFrame = requestAnimationFrame(() => { if(this.opened) this.list.classList.add('is-open'); }); });
    }

    close(focus = true) {
      this.opened = false; if(active === this) active = null;
      cancelAnimationFrame(this.openFrame); clearTimeout(this.closeTimer);
      this.button.setAttribute('aria-expanded','false'); this.button.removeAttribute('aria-activedescendant'); this.list.classList.remove('is-open');
      this.list.inert = true; this.list.setAttribute('aria-hidden','true');
      const finish = () => { if(this.opened) return; if(this.list.hasAttribute('popover') && this.list.matches(':popover-open')) this.list.hidePopover(); this.list.hidden = true; };
      if(reducedMotion()) finish(); else this.closeTimer = setTimeout(finish,220);
      if(focus && this.button.isConnected) this.button.focus();
    }

    choose(index) {
      const item = this.items.find(item => item.option.index === index && !item.disabled);
      if(!item || this.button.disabled) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index; this.sync(); this.close();
      // Native forms, facet sorting and Shopify variant pickers keep their own handlers.
      if(changed) {
        this.select.dispatchEvent(new Event('input',{bubbles:true}));
        this.select.dispatchEvent(new Event('change',{bubbles:true}));
      }
    }

    key(event) {
      if(event.isComposing || event.keyCode === 229 || event.ctrlKey || event.metaKey) return;
      const key = event.key;
      if(key === 'Tab') { this.close(false); return; }
      if(key === 'Escape') { if(this.opened) { event.preventDefault(); event.stopPropagation(); this.close(); } return; }
      if(['ArrowDown','ArrowUp','Home','End','Enter',' '].includes(key)) {
        event.preventDefault(); event.stopPropagation();
        if(!this.opened) { this.open(); if(key !== 'Home' && key !== 'End') return; }
        else if(key === 'Enter' || key === ' ') { this.choose(this.cursor); return; }
        const enabled = this.items.filter(item => !item.disabled);
        const current = enabled.findIndex(item => item.option.index === this.cursor);
        const index = key === 'Home' ? 0 : key === 'End' ? enabled.length - 1 : Math.max(0,Math.min(enabled.length - 1,current + (key === 'ArrowUp' ? -1 : 1)));
        this.setCursor(enabled[index]?.option.index); return;
      }
      if(key.length === 1 && !event.altKey) {
        event.preventDefault(); event.stopPropagation();
        if(!this.opened) this.open();
        clearTimeout(this.searchTimer); this.search = (this.search || '') + key.toLocaleLowerCase();
        const matches = this.items.filter(item => !item.disabled && item.option.label.toLocaleLowerCase().startsWith(this.search));
        if(matches.length) this.setCursor(matches[0].option.index);
        this.searchTimer = setTimeout(() => { this.search = ''; },650);
      }
    }

    destroy() {
      this.close(false); clearTimeout(this.searchTimer); this.observer.disconnect(); this.abort.abort();
      this.select.classList.remove('cv-select__native');
      for (const [name,value] of [['tabindex',this.oldTabIndex],['aria-hidden',this.oldAriaHidden]]) { if(value === null) this.select.removeAttribute(name); else this.select.setAttribute(name,value); }
      known.delete(this.select); instances.delete(this);
    }
  }

  function scan() {
    for(const instance of instances) {
      if(!instance.select.isConnected || instance.select.parentElement !== instance.wrapper) instance.destroy();
      else if(instance.button.disabled !== instance.select.matches(':disabled')) instance.sync();
    }
    for(const select of document.querySelectorAll('select[data-cv-select]')) {
      if(!known.has(select) && !select.multiple && select.size <= 1) new CalvielleSelect(select);
    }
  }
  scan();
  new MutationObserver(() => { cancelAnimationFrame(scanFrame); scanFrame = requestAnimationFrame(scan); }).observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['disabled']});
  document.addEventListener('pointerdown', event => { if(active && !active.wrapper.contains(event.target)) active.close(false); });
  document.addEventListener('click', event => {
    const label = event.target.closest('label'), instance = label?.control && known.get(label.control);
    if(instance && !instance.wrapper.contains(event.target)) { event.preventDefault(); instance.button.focus(); }
  });
  window.addEventListener('resize', () => active?.close(false));
  window.addEventListener('scroll', event => { if(active && !active.list.contains(event.target)) active.close(false); },true);
})();
